import React, { Component } from 'react';
import PropTypes from 'prop-types';
import randomId from 'random-id';
import { ErrorMessage, Field, Form, Formik } from 'formik';
import * as Yup from 'yup';
import Swal from 'sweetalert2';
import { Nav, NavItem, NavLink, TabContent, TabPane } from 'reactstrap';
import classnames from 'classnames';
import * as moment from 'moment';
import { dynamicSort } from '../../../common/utils/api_utils';
import cookie from 'react-cookies';
import { getBuilderConfigURL } from '../../../common/utils/builder-config-url-util';
import history from '../../../common/history';
import CustomSelect from '../../../common/formElements/CustomSelect';
import CustomSelectWithValues from '../../../common/formElements/CustomSelectWithValues';
import CustomLoader from '../../common/CustomLoader';
import DocsLink from '../../common/DocsLink';
import { showWarning } from '../../../common/utils/alert';
import TooltipInfo from '../../../common/formElements/TooltipInfo';
import { SNIPPET } from 'drapcode-constant';

export default class SnippetTemplate extends Component {
  static propTypes = {
    editor: PropTypes.object.isRequired,
    actions: PropTypes.object.isRequired,
    projectId: PropTypes.string.isRequired,
  };

  state = {
    snippetTypes: [
      { value: 'SNIPPET', label: 'Snippet' },
      { value: 'MODAL', label: 'Modal' },
      { value: 'CUSTOM_SEGMENT', label: 'Custom Segment' },
      { value: 'SUB_PAGE', label: 'Sub Page' },
      { value: 'PDF_TEMPLATE', label: 'PDF Template' },
      { value: 'PAGE_LAYOUT', label: 'Page Layout' },
    ],
    activeTab: '1',
    snapshotName: `Snapshot-${moment().format('YYYY-MMMM-DD-HH-mm-ss-SSS')}`,
    snapshotNameError: '',
    collectionId: '',
    externalScriptURL: '',
    customScript: '',
    collectionFrom: 'COLLECTION',
    externalApiId: '',
    snippetType: 'SNIPPET',
  };

  toggle = (activeTab) => {
    this.setState({ activeTab });
  };

  componentDidMount = () => {
    const { editor, projectId, version, actions } = this.props;
    const snippet = editor.selectedSnippet;
    if (snippet.uuid) {
      const {
        name,
        snippetType,
        collectionId,
        externalScriptURL,
        customScript,
        collectionFrom,
        externalApiId,
      } = snippet;

      this.setState({
        snapshotName: `${name}-Snapshot-${moment().format('YYYY-MMMM-DD-HH-mm-ss-SSS')}`,
        snippetType: snippetType ? snippetType : 'SNIPPET',
        externalScriptURL: externalScriptURL,
        customScript: customScript,
        collectionId: collectionId,
        collectionFrom: collectionFrom ? collectionFrom : 'COLLECTION',
        externalApiId: externalApiId,
      });
    }
    actions.loadDefaultPage({ projectId, version });
    actions.getPermissionFlags({ projectId });
  };

  handleChange = (name, value) => {
    if (name === 'snapshotName') {
      this.setState({ [name]: value, snapshotNameError: '' });
    } else if (name === 'collectionId') {
      this.setState({
        [name]: value,
        externalApiId: '',
        collectionFrom: 'COLLECTION',
      });
    } else if (['externalScriptURL', 'customScript'].includes(name)) {
      this.setState({
        [name]: value,
      });
    } else {
      this.setState({ [name]: value });
    }
  };

  handleSnapshot = () => {
    const { snapshotName } = this.state;
    const { editor, actions, projectId, version } = this.props;
    const { selectedSnippet } = editor;
    if (snapshotName === '' || !snapshotName) {
      this.setState({ snapshotNameError: 'This field is required' });
      return;
    }
    let snippetSnapshotValues = {
      name: snapshotName,
      projectId,
      snippetId: selectedSnippet.uuid,
    };
    actions.createSnippetSnapshot({
      projectId,
      version,
      data: snippetSnapshotValues,
    });
  };

  lastSnippet = () => {
    const { snippets } = this.props.editor;
    if (snippets.length > 0) {
      return snippets[snippets.length - 1].uuid;
    }
    return null;
  };

  createSnippet = (values, resetForm) => {
    const { editor, actions, projectId, version } = this.props;
    const { selectedSnippet } = editor;
    const { externalScriptURL, customScript, snippetType } = this.state;
    let randomModalId = randomId(8, '');
    if (selectedSnippet && selectedSnippet.uuid) {
      actions.updateSnippet({
        name: values.name,
        projectId,
        version,
        templateId: selectedSnippet.uuid,
        collectionId: values.collectionId,
        externalScriptURL: externalScriptURL,
        customScript: customScript,
        collectionFrom: values.collectionFrom,
        externalApiId:
          values.collectionFrom && values.collectionFrom === 'EXTERNAL_API'
            ? values.externalApiId
            : '',
      });
    } else if (['MODAL', 'SUB_PAGE', 'PDF_TEMPLATE'].includes(snippetType)) {
      const modalContent = {
        'nocode-html': `<div role="dialog" tabindex="-1" id="${randomModalId}" class="modal fade show"><div role="document" class="modal-dialog"><div class="modal-content"><div class="modal-header"><h5 class="modal-title">Modal Title</h5><button role="button" type="button" aria-label="Close" class="btn close">x</button></div><div class="modal-body"><p>Modal body text goes here.</p></div><div class="modal-footer"><button role="button" type="button" class="btn btn-secondary">Close</button></div></div></div></div>`,
        'nocode-components': `[{"type":"modal","content":"","classes":[{"name":"modal","label":"modal","type":1,"active":true,"private":false,"protected":false},{"name":"fade","label":"fade","type":1,"active":true,"private":false,"protected":false},{"name":"show","label":"show","type":1,"active":true,"private":false,"protected":false}],"attributes":{"role":"dialog","tabindex":"-1","id":"${randomModalId}"},"components":[{"type":"modal-dialog","content":"","classes":[{"name":"modal-dialog","label":"modal-dialog","type":1,"active":true,"private":false,"protected":false}],"attributes":{"role":"document"},"components":[{"type":"modal-content","content":"","classes":[{"name":"modal-content","label":"modal-content","type":1,"active":true,"private":false,"protected":false}],"components":[{"type":"modal-header","content":"","classes":[{"name":"modal-header","label":"modal-header","type":1,"active":true,"private":false,"protected":false}],"components":[{"tagName":"h5","type":"header","content":"Modal Title","classes":[{"name":"modal-title","label":"modal-title","type":1,"active":true,"private":false,"protected":false}],"custom-name":"Modal Title"},{"tagName":"button","type":"bootstrap_button","content":"x","classes":[{"name":"btn","label":"btn","type":1,"active":true,"private":false,"protected":false},{"name":"close","label":"close","type":1,"active":true,"private":false,"protected":false}],"attributes":{"role":"button","type":"button","aria-label":"Close"}}]},{"type":"modal-body","content":"","classes":[{"name":"modal-body","label":"modal-body","type":1,"active":true,"private":false,"protected":false}],"components":[{"type":"paragraph","content":"Modal body text goes here."}]},{"type":"modal-footer","content":"","classes":[{"name":"modal-footer","label":"modal-footer","type":1,"active":true,"private":false,"protected":false}],"components":[{"tagName":"button","type":"bootstrap_button","droppable":1,"content":"Close","classes":[{"name":"btn","label":"btn","type":1,"active":true,"private":false,"protected":false},{"name":"btn-secondary","label":"btn-secondary","type":1,"active":true,"private":false,"protected":false}],"attributes":{"role":"button","type":"button"}}]}]}]}]}]`,
        'nocode-css': `* { box-sizing: border-box; } body {margin: 0;}.modal.show{display:block;background-color:rgba(0, 0, 0, 0.6);overflow-x: hidden;overflow-y: auto;z-index: 1000}`,
        'nocode-styles': `[{"selectors":[{"name":"modal","label":"modal","type":1,"active":true,"private":false,"protected":false},{"name":"fade","label":"fade","type":1,"active":true,"private":false,"protected":false},{"name":"show","label":"show","type":1,"active":true,"private":false,"protected":false}],"style":{"display":"block","background-color":"rgba(0, 0, 0, 0.6)","overflow-x":"hidden","overflow-y":"auto","z-index":"1000"}}]`,
      };
      let snippetConfig = {
        name: values.name,
        snippetType,
        collectionId: values.collectionId,
        projectId,
        version,
        externalScriptURL: externalScriptURL,
        customScript: customScript,
        collectionFrom: values.collectionFrom,
        externalApiId:
          values.collectionFrom && values.collectionFrom === 'EXTERNAL_API'
            ? values.externalApiId
            : '',
        snippetCloneFrom: values.snippetCloneFrom,
        snippetRestoreFrom: values.snippetRestoreFrom,
        snippetCloneFromPage: values.snippetCloneFromPage,
        snippetCloneFromPageSnapshots: values.snippetCloneFromPageSnapshots,
      };

      snippetConfig =
        snippetType === 'MODAL' ? { ...snippetConfig, content: modalContent } : snippetConfig;
      this.props.actions.createSnippetTemplate({ ...snippetConfig }).then(() => {
        resetForm();
        history.push(`/editor/${version}/v/${projectId}/snippets/${this.lastSnippet()}`);
      });
    } else {
      actions
        .createSnippetTemplate({
          name: values.name,
          snippetType,
          projectId,
          version,
          snippetCloneFrom: values.snippetCloneFrom,
          snippetRestoreFrom: values.snippetRestoreFrom,
          snippetCloneFromPage: values.snippetCloneFromPage,
          snippetCloneFromPageSnapshots: values.snippetCloneFromPageSnapshots,
        })
        .then(() => {
          resetForm();
          history.push(`/editor/${version}/v/${projectId}/snippets/${this.lastSnippet()}`);
        });
    }
  };

  handleDelete = (snippet) => {
    const {
      projectId,
      version,
      actions,
      editor: { snippets, defaultPage },
    } = this.props;
    const preConfirm = () => {
      return actions
        .deleteSnippet({
          projectId,
          version,
          templateId: snippet.uuid,
        })
        .then(() => {
          const lastSnippetUuid = this.lastSnippet();
          if (lastSnippetUuid) {
            history.replace(`/editor/${version}/v/${projectId}/snippets/${lastSnippetUuid}`);
          } else {
            history.replace(`/editor/${version}/v/${projectId}/pages/${defaultPage.uuid}`);
          }
        });
    };
    showWarning("You won't be able to revert this.", preConfirm, 'Yes, delete it!').then(
      (result) => {
        if (result.isConfirmed) {
          this.props.actions.toggleEmailSetting();
        }
      },
    );
  };

  collectionsOptions = () => {
    const { collections } = this.props.editor;
    let options = [{ value: '', label: 'Select a collection' }];
    const collectionOptions =
      collections.length > 0
        ? collections.sort(dynamicSort('collectionName')).map((collection) => {
            return { value: collection.collectionName, label: collection.collectionTitle };
          })
        : [];
    return [...options, ...collectionOptions];
  };

  showAdvanceSnippetSetting = () => {
    const showSnippetSnapshotCreateLoader = this.props.editor.deleteSnippetPending;
    const snippet = this.props.editor.selectedSnippet;
    const { snapshotName, snapshotNameError, activeTab } = this.state;
    if (snippet.uuid) {
      return (
        <div className="row pt-2">
          <div className="col-12">
            <div className="form-group">
              <label htmlFor="titleTag">Snapshot Name</label>
              <input
                type="text"
                name="snapshotName"
                className="form-control form-control-sm"
                placeholder="Snapshot Name"
                value={snapshotName}
                onChange={(e) => this.handleChange(e.target.name, e.target.value)}
              />
              {snapshotNameError && <p className="text-error">{snapshotNameError}</p>}
              <div className="row p-2">
                <div className="col-12">
                  <div className="text-right">
                    {snippet.uuid && activeTab === '2' ? (
                      <button
                        type="button"
                        className="btn btn-theme-submit btn-sm"
                        disabled={showSnippetSnapshotCreateLoader}
                        onClick={this.handleSnapshot}
                      >
                        <i className="wb webconnect-plus"></i>&nbsp;Create Snapshot&nbsp;
                        <CustomLoader loading={showSnippetSnapshotCreateLoader} />
                      </button>
                    ) : (
                      ''
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    } else return '';
  };

  showAdvanceSnippetJSSetting = () => {
    const showSnippetLoader = this.props.editor.createSnippetTemplatePending;
    const deleteSnippetLoader = this.props.editor.deleteSnippetPending;
    const snippet = this.props.editor.selectedSnippet;
    const { externalScriptURL, customScript } = this.state;

    let snippetJSSchema = {
      name: snippet && snippet.name ? snippet.name : '',
      snippetType: snippet && snippet.snippetType ? snippet.snippetType : 'SNIPPET',
      snippetClone: '',
      snippetCloneFrom: '',
      snippetRestoreFrom: '',
      collectionId: snippet && snippet.collectionId ? snippet.collectionId : '',
      externalScriptURL: snippet && snippet.externalScriptURL ? snippet.externalScriptURL : '',
      customScript: snippet && snippet.customScript ? snippet.customScript : '',
      collectionFrom: snippet.collectionFrom,
      externalApiId:
        snippet.collectionFrom && snippet.collectionFrom === 'EXTERNAL_API'
          ? snippet.externalApiId
          : '',
    };

    if (snippet.uuid) {
      return (
        <Formik
          enableReinitialize={true}
          initialValues={snippetJSSchema}
          validationSchema={this.prepareValidationForFields()}
          onSubmit={(values, { setSubmitting, resetForm }) => {
            setSubmitting(false);
            this.createSnippet(values, resetForm);
          }}
        >
          {({ isSubmitting, values, setFieldValue, setFieldTouched, errors }) => (
            <Form className="pt-2">
              <div className="form-group">
                <label htmlFor="externalScriptURL">Script URL(s)</label>
                <small>
                  ( Add external script URL(s) here, enter multiple scripts separated by comma ' , '
                  )
                </small>
                <textarea
                  type="text"
                  name="externalScriptURL"
                  className="form-control"
                  autoComplete="off"
                  rows="2"
                  value={externalScriptURL}
                  onChange={(e) => this.handleChange(e.target.name, e.target.value)}
                />
              </div>
              <div className="form-group">
                <label htmlFor="customScript">JavaScript Code</label>
                <small>( Add custom JavaScript code here )</small>
                <textarea
                  type="text"
                  name="customScript"
                  className="form-control"
                  autoComplete="off"
                  rows="10"
                  value={customScript}
                  onChange={(e) => this.handleChange(e.target.name, e.target.value)}
                />
              </div>

              <div style={{ textAlign: 'right' }}>
                <button
                  type="button"
                  className="btn btn-theme-cancel btn-sm"
                  onClick={() => this.props.actions.toggleEmailSetting()}
                >
                  <span>
                    <i className="wb webconnect-times"></i>&nbsp;Close
                  </span>
                </button>
                &nbsp;
                {snippet && snippet.uuid && (
                  <button
                    type="button"
                    className="btn btn-theme-delete btn-sm"
                    disabled={deleteSnippetLoader}
                    onClick={() => this.handleDelete(snippet)}
                  >
                    <i className="wb webconnect-trash-regular"></i> Delete Snippet&nbsp;
                    <CustomLoader loading={deleteSnippetLoader} />
                  </button>
                )}
                &nbsp;
                <button
                  type="submit"
                  className="btn btn-theme-submit btn-sm"
                  disabled={isSubmitting || showSnippetLoader}
                >
                  <i className="wb webconnect-plus"></i>&nbsp;
                  {snippet && snippet.uuid ? 'Update' : 'Create'} Snippet&nbsp;
                  <CustomLoader loading={showSnippetLoader} />
                </button>
              </div>
            </Form>
          )}
        </Formik>
      );
    } else return '';
  };

  snippetCopyOptions = (value) => {
    let options = [
      { value: '', label: '- Select One -' },
      { value: 'fromExistingSnippets', label: `${value} from Existing Snippets` },
      { value: 'fromSnippetSnapshots', label: `${value} from Snippet Snapshots` },
      { value: 'fromExistingPages', label: `${value} from Existing Pages` },
      { value: 'fromPageSnapshots', label: `${value} from Page Snapshots` },
    ];
    return options;
  };

  snippetOptions = () => {
    const snippetsToSort = [...this.props.editor.snippets];
    let options = [{ value: '', label: '- Select Snippet -' }];
    if (snippetsToSort && snippetsToSort.length > 0) {
      options = options.concat(
        snippetsToSort.sort(dynamicSort('name')).map((e) => {
          return { value: e.uuid, label: e.name };
        }),
      );
    }
    return options;
  };

  snippetSnapshotOptions = () => {
    const { snippetSnapshots } = this.props.editor;
    let options = [{ value: '', label: '- Select Snippet Snapshot -' }];
    if (snippetSnapshots.length > 0) {
      const sortedSnapshots = snippetSnapshots.sort(dynamicSort('createdAt')).reverse();
      options = options.concat(
        sortedSnapshots.map((snapshot) => ({
          value: snapshot.uuid,
          label: snapshot.name,
        })),
      );
    }
    return options;
  };

  pageOptions = () => {
    let options = [{ value: '', label: '- Select Page -' }];
    const { pages } = this.props.editor;
    if (pages.length > 0) {
      options = options.concat(
        pages.sort(dynamicSort('name')).map((page) => {
          return { value: page.uuid, label: page.name };
        }),
      );
    }
    return options;
  };

  pageSnapshotOptions = () => {
    let options = [{ value: '', label: '- Select Page Snapshot -' }];
    const { pageSnapshots } = this.props.editor;
    if (pageSnapshots.length > 0) {
      options = options.concat(
        pageSnapshots.sort(dynamicSort('name')).map((snapshot) => {
          return { value: snapshot.uuid, label: snapshot.name };
        }),
      );
    }
    return options;
  };

  onSnippetCloneTypeChange = (setFieldValue, values, value) => {
    if (value === '') {
      this.setState({ snippetType: 'SNIPPET' });
      setFieldValue('snippetClone', '');
    } else {
      setFieldValue('snippetClone', value);
    }
    setFieldValue('snippetCloneFrom', '');
    setFieldValue('snippetRestoreFrom', '');
  };

  onCollectionChange = (setFieldValue, values, value) => {
    if (value === '') {
      setFieldValue('collectionId', '');
    } else {
      setFieldValue('collectionId', value);
    }
  };

  showCloneFrom = (values, setFieldValue, setFieldTouched) => {
    const { selectedSnippet } = this.props.editor;
    if (!selectedSnippet.uuid) {
      return (
        <div className="form-group">
          <label>Copy Snippet Content (Optional)</label>
          <span data-tip="Snippet Content" title="Allow you to create a reusable page">
            <i className="webconnect webconnect-info-circle"></i>
          </span>
          <CustomSelectWithValues
            name={'snippetClone'}
            isMulti={false}
            onBlur={setFieldTouched}
            setFieldValue={setFieldValue}
            //onChange={setFieldValue}
            options={this.snippetCopyOptions('Copy')}
            value={values['snippetClone']}
            onChangeOption={this.onSnippetCloneTypeChange}
            formValues={values}
          />

          <p className="text-danger">
            <ErrorMessage name={'name'} />
          </p>
        </div>
      );
    }
  };

  showExistingSnippetOptions = (values, setFieldValue, setFieldTouched) => {
    const { selectedSnippet } = this.props.editor;
    if (!selectedSnippet.uuid && values['snippetClone'] === 'fromExistingSnippets') {
      return (
        <div className="form-group">
          <CustomSelect
            name={'snippetCloneFrom'}
            isMulti={false}
            onBlur={setFieldTouched}
            onChange={setFieldValue}
            options={this.snippetOptions()}
            value={values['snippetCloneFrom']}
          />
        </div>
      );
    } else {
      return '';
    }
  };

  showSnippetSnapshotsOptions = (values, setFieldValue, setFieldTouched) => {
    const { selectedSnippet } = this.props.editor;
    if (!selectedSnippet.uuid && values['snippetClone'] === 'fromSnippetSnapshots') {
      return (
        <div className="form-group">
          <CustomSelect
            name={'snippetRestoreFrom'}
            isMulti={false}
            onBlur={setFieldTouched}
            onChange={setFieldValue}
            options={this.snippetSnapshotOptions()}
            value={values['snippetRestoreFrom']}
          />
        </div>
      );
    } else {
      return '';
    }
  };

  showExistingPagesOptions = (values, setFieldValue, setFieldTouched) => {
    const { selectedSnippet } = this.props.editor;
    if (!selectedSnippet.uuid && values['snippetClone'] === 'fromExistingPages') {
      return (
        <div className="form-group">
          <CustomSelect
            name={'snippetCloneFromPage'}
            isMulti={false}
            onBlur={setFieldTouched}
            onChange={setFieldValue}
            options={this.pageOptions()}
            value={values['snippetCloneFromPage']}
          />
        </div>
      );
    } else {
      return '';
    }
  };

  showPageSnapshotsOptions = (values, setFieldValue, setFieldTouched) => {
    const { selectedSnippet } = this.props.editor;
    if (!selectedSnippet.uuid && values['snippetClone'] === 'fromPageSnapshots') {
      return (
        <div className="form-group">
          <CustomSelect
            name={'snippetCloneFromPageSnapshots'}
            isMulti={false}
            onBlur={setFieldTouched}
            onChange={setFieldValue}
            options={this.pageSnapshotOptions()}
            value={values['snippetCloneFromPageSnapshots']}
          />
        </div>
      );
    } else {
      return '';
    }
  };

  showCollectionOptions = (values, setFieldValue, setFieldTouched) => {
    let token = cookie.load('token');
    if (!token) {
      token = '';
    }
    const { snippetType } = this.state;
    const { collectionId } = values;
    if (['MODAL', 'SUB_PAGE', 'PDF_TEMPLATE'].includes(snippetType)) {
      //TODO: Vijay please change
      return (
        <div className="form-group">
          <label htmlFor="collectionId">Bind Collection</label>
          <a
            href={`${getBuilderConfigURL()}collections/${
              this.props.projectId
            }/create?token=${token}`}
            className="direction-custom-link"
          >
            Create Collection
          </a>
          <CustomSelect
            name={'collectionId'}
            isMulti={false}
            onBlur={setFieldTouched}
            onChange={setFieldValue}
            options={this.collectionsOptions()}
            value={collectionId}
          />
        </div>
      );
    } else {
      return '';
    }
  };

  externalAPIOptions = (values, setFieldValue, setFieldTouched) => {
    const { editor } = this.props;
    const { externalApis } = editor ? editor : '';
    const { collectionId } = values;

    let options = [{ value: '', label: 'Select an External API' }];
    const externalApiOptions =
      externalApis.length > 0
        ? externalApis
            .filter(
              (externalAPI) =>
                [
                  'REST_API',
                  'AIRTABLE',
                  'XANO',
                  'NOTION',
                  'APP_WRITE',
                  'SUPABASE',
                  'XATA',
                  'DIRECTUS',
                ].includes(externalAPI.externalApiType) &&
                externalAPI.responseDataMapping.selectedCollectionName &&
                externalAPI.responseDataMapping.selectedCollectionName === collectionId,
            )
            .sort(dynamicSort('externalApiName'))
            .map((externalAPI) => {
              return { value: externalAPI.uuid, label: externalAPI.externalApiName };
            })
        : [];
    return [...options, ...externalApiOptions];
  };

  collectionFromOptions = () => {
    let options = [
      { value: '', label: 'Select Bind Data From' },
      { value: 'COLLECTION', label: 'Collection (Default)' },
      { value: 'EXTERNAL_API', label: 'External API' },
    ];
    return [...options];
  };

  prepareValidationForFields = () => {
    let validationSchema = {
      name: Yup.string()
        .required("Name can't be blank")
        .max(100, 'Name can not be more than 100 characters'),
      //snippetType: Yup.string().required("Type can't be blank"),
    };
    return Yup.object().shape(validationSchema);
  };

  bindCollectionDataFrom = (values, setFieldValue, setFieldTouched) => {
    const { snippetType } = this.state;
    const { collectionFrom, collectionId } = values;
    if (collectionId && ['MODAL', 'SUB_PAGE', 'PDF_TEMPLATE'].includes(snippetType)) {
      return (
        <div className="form-group">
          <label htmlFor="collectionFrom">Bind Data From</label>
          <CustomSelect
            name={'collectionFrom'}
            isMulti={false}
            onBlur={setFieldTouched}
            onChange={setFieldValue}
            options={this.collectionFromOptions()}
            value={collectionFrom}
          />
        </div>
      );
    } else {
      return '';
    }
  };

  bindExternalAPI = (values, setFieldValue, setFieldTouched) => {
    let token = cookie.load('token');
    if (!token) {
      token = '';
    }
    const { snippetType } = this.state;
    const { collectionFrom, collectionId, externalApiId } = values;
    if (
      ['MODAL', 'SUB_PAGE', 'PDF_TEMPLATE'].includes(snippetType) &&
      collectionId &&
      collectionFrom === 'EXTERNAL_API'
    ) {
      return (
        <div className="form-group">
          <label htmlFor="externalApiId">Bind External API</label>
          <a
            href={`${getBuilderConfigURL()}externalapi/${
              this.props.projectId
            }/create?token=${token}`}
            className="direction-custom-link"
          >
            Create External API
          </a>
          <CustomSelect
            name={'externalApiId'}
            isMulti={false}
            onBlur={setFieldTouched}
            onChange={setFieldValue}
            options={this.externalAPIOptions(values, setFieldValue, setFieldTouched)}
            value={externalApiId}
          />
        </div>
      );
    } else {
      return '';
    }
  };

  isSelectedSnippet = (type) => {
    const snippet = this.props.editor.selectedSnippet;
    const { snippetType } = this.state;

    return snippet && snippet.uuid
      ? snippet.snippetType === type.value
      : snippetType === type.value;
  };

  render() {
    const description = `Go to the Delete Permissions in Settings and Enable it.`;
    const { activeTab, snippetType, snippetTypes } = this.state;
    const showSnippetLoader = this.props.editor.createSnippetTemplatePending;
    const deleteSnippetLoader = this.props.editor.deleteSnippetPending;
    const snippet = this.props.editor.selectedSnippet;
    const permissionFlags = this.props.editor.permissionFlags;
    const { name, collectionId, externalScriptURL, customScript, collectionFrom, externalApiId } =
      snippet ? snippet : '';
    let flag;
    if (permissionFlags && permissionFlags.length > 0) {
      flag = permissionFlags.includes(SNIPPET);
    }
    let snippetSchema = {
      name: name ? name : '',
      snippetClone: '',
      snippetCloneFrom: '',
      snippetRestoreFrom: '',
      snippetCloneFromPage: '',
      snippetCloneFromPageSnapshots: '',
      collectionId: collectionId ? collectionId : '',
      externalScriptURL: externalScriptURL ? externalScriptURL : '',
      customScript: customScript ? customScript : '',
      collectionFrom: collectionFrom ? collectionFrom : 'COLLECTION',
      externalApiId: externalApiId ? externalApiId : '',
    };
    if (snippet) {
      return (
        <div>
          <Nav tabs>
            <NavItem>
              <NavLink
                className={classnames({ active: activeTab === '1' })}
                onClick={() => this.toggle('1')}
              >
                General
              </NavLink>
            </NavItem>
            {snippet.uuid ? (
              <NavItem>
                <NavLink
                  className={classnames({ active: activeTab === '2' })}
                  onClick={() => this.toggle('2')}
                >
                  Advance
                </NavLink>
              </NavItem>
            ) : (
              ''
            )}
          </Nav>
          <TabContent activeTab={activeTab}>
            <TabPane tabId="1">
              <Formik
                enableReinitialize={true}
                initialValues={snippetSchema}
                validationSchema={this.prepareValidationForFields()}
                onSubmit={(values, { setSubmitting, resetForm }) => {
                  setSubmitting(false);
                  this.createSnippet(values, resetForm);
                }}
              >
                {({ isSubmitting, values, setFieldValue, setFieldTouched, errors }) => (
                  <Form className="pt-2">
                    <div className="form-group">
                      <label htmlFor="name">Snippet Name</label>
                      <span data-tip="Snippet Name" title="Type a name of your choice">
                        <i className="webconnect webconnect-info-circle"></i>
                      </span>
                      <Field
                        type="text"
                        name="name"
                        className="form-control form-control-sm"
                        placeholder="Snippet Name"
                      />

                      <p className="text-danger">
                        <ErrorMessage name={'name'} />
                      </p>
                    </div>

                    {values['snippetClone'] === '' && (
                      <div className="form-group">
                        <label htmlFor="name">Type</label>
                        <span data-tip="Type" title="Choose the type of snippet you want to create">
                          <i className="webconnect webconnect-info-circle"></i>
                        </span>
                        <br />
                        {snippetTypes.map((type, index) => (
                          <React.Fragment key={type.value}>
                            <input
                              style={index > 0 ? { marginLeft: '2rem' } : {}}
                              type="radio"
                              name="snippetType"
                              checked={this.isSelectedSnippet(type)}
                              value={type.value}
                              onChange={(e) => this.setState({ snippetType: e.target.value })}
                              disabled={snippet && snippet.uuid ? true : false}
                            />
                            <label
                              className={
                                this.isSelectedSnippet(type)
                                  ? 'ml-1 label badge-info badge-pill text-light pl-0 px-3'
                                  : 'ml-1'
                              }
                            >
                              {' '}
                              {type.label}{' '}
                            </label>
                          </React.Fragment>
                        ))}
                      </div>
                    )}
                    {this.showCollectionOptions(values, setFieldValue, setFieldTouched)}
                    {this.bindCollectionDataFrom(values, setFieldValue, setFieldTouched)}
                    {this.bindExternalAPI(values, setFieldValue, setFieldTouched)}
                    {this.showCloneFrom(values, setFieldValue, setFieldTouched)}
                    {this.showExistingSnippetOptions(values, setFieldValue, setFieldTouched)}
                    {this.showSnippetSnapshotsOptions(values, setFieldValue, setFieldTouched)}
                    {this.showExistingPagesOptions(values, setFieldValue, setFieldTouched)}
                    {this.showPageSnapshotsOptions(values, setFieldValue, setFieldTouched)}
                    <div style={{ textAlign: 'right' }}>
                      <DocsLink link1="https://academy.drapcode.com/docs/app-builder/advanced-component-snippet#adding-a-snippet" />
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <button
                        type="button"
                        className="btn btn-theme-cancel btn-sm"
                        onClick={() => this.props.actions.toggleEmailSetting()}
                      >
                        <span>
                          <i className="wb webconnect-times"></i>&nbsp;Close
                        </span>
                      </button>
                      &nbsp;
                      {snippet && snippet.uuid && (
                        <button
                          type="button"
                          className="btn btn-theme-delete btn-sm"
                          disabled={deleteSnippetLoader}
                          onClick={() => (flag ? this.handleDelete(snippet) : undefined)}
                          style={{ cursor: flag ? 'pointer' : 'not-allowed' }}
                        >
                          <i className="wb webconnect-trash-regular"></i> Delete Snippet&nbsp;
                          {flag ? null : (
                            <TooltipInfo
                              title={description}
                              headerText={`Permission Disabled!`}
                              showHeader={true}
                              showArrow={true}
                            />
                          )}
                          <CustomLoader loading={deleteSnippetLoader} />
                        </button>
                      )}
                      &nbsp;
                      <button
                        type="submit"
                        className="btn btn-theme-submit btn-sm"
                        disabled={isSubmitting || showSnippetLoader}
                      >
                        <i className="wb webconnect-plus"></i>&nbsp;
                        {snippet && snippet.uuid ? 'Update' : 'Create'} Snippet&nbsp;
                        <CustomLoader loading={showSnippetLoader} />
                      </button>
                    </div>
                  </Form>
                )}
              </Formik>
            </TabPane>
            <TabPane tabId="2">
              {this.showAdvanceSnippetSetting()}
              {this.showAdvanceSnippetJSSetting()}
            </TabPane>
          </TabContent>
        </div>
      );
    } else {
      return '';
    }
  }
}
